import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import type {CancelSubscriptionPanelState} from '@Panels/cancel-subscription-panel/cancel-subscription-panel.types';
import {
  cancelSubscription,
  getCheaperPlanChangeCost,
  initCancelSubscriptionPanel,
  pauseSubscription,
  submitCancellationSupportRequest,
  submitPostCancellationFeedback,
  switchToCheaperPlan,
} from '@Panels/cancel-subscription-panel/cancel-subscription-panel-thunk';
import {CancelSubscriptionPanelStep, CancelSurveyReasonIDs, PauseIntervals} from '@Libraries/cancel-subscription-library';
import {TransitionDirection} from '@Panels/transitionable-panel-v2/transitionable-panel-v2.types';

const initialState: CancelSubscriptionPanelState = {
  isReady: false,
  hasInitFailed: false,
  isWaitingForAJAXResponse: false,
  currentStep: CancelSubscriptionPanelStep.FEATURE_USAGE,
  cancelReason: CancelSurveyReasonIDs.NONE,
  cancelReasonComments: '',
  shuffledReasonIds: [],
  featureUsages: null,
  pauseInterval: PauseIntervals.ONE_MONTH,
  subscriptionExpiresOnTimestamp: 0,
  pauseEndTimestamp: 0,
  transitionDirection: TransitionDirection.FORWARD,
  isRenewalInvoicePending: false,
  canPause: false,
  showPricingIncreaseBanner: false,
  isTeamAccount: false,
  isPremiumPlus: false,
  retentionOffer: null,
  cheaperPlans: [],
  switchPlanTitle: null,
  switchPlanSubtitle: null,
  remainingCredits: 0,
  remainingEmailSends: 0,
};

export const cancelSubscriptionPanelSlice = createSlice({
  name: 'cancelSubscriptionPanel',
  initialState,
  reducers: {
    updateCancelReason: (state, {payload}: PayloadAction<CancelSurveyReasonIDs>) => {
      state.cancelReason = payload;
    },
    updateCancelReasonComments: (state, {payload}: PayloadAction<string>) => {
      state.cancelReasonComments = payload;
    },
    setShuffledReasonIds: (state, {payload}: PayloadAction<CancelSurveyReasonIDs[]>) => {
      state.shuffledReasonIds = payload;
    },
    updateStep: (
      state,
      {
        payload,
      }: PayloadAction<{
        step: CancelSubscriptionPanelStep;
        direction: TransitionDirection;
      }>
    ) => {
      state.currentStep = payload.step;
      state.transitionDirection = payload.direction;
    },
    updatePauseInterval: (state, {payload}: PayloadAction<PauseIntervals>) => {
      state.pauseInterval = payload;
    },
    updateIsWaitingForAJAXResponse: (state, {payload}: PayloadAction<boolean>) => {
      state.isWaitingForAJAXResponse = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(initCancelSubscriptionPanel.pending, (state, {meta}) => {
      state.isReady = false;
      state.hasInitFailed = false;
      state.cancelReason = CancelSurveyReasonIDs.NONE;
      state.cancelReasonComments = '';
      state.currentStep = meta.arg.initialStep;
      state.pauseInterval = PauseIntervals.ONE_MONTH;
    });
    builder.addCase(initCancelSubscriptionPanel.fulfilled, (state, {payload, meta}) => {
      state.isReady = true;
      state.featureUsages = payload.featureUsages;
      state.subscriptionExpiresOnTimestamp = payload.subscriptionExpiresOnTimestamp;
      state.isRenewalInvoicePending = payload.isRenewalInvoicePending;
      state.canPause = payload.canPause;
      state.showPricingIncreaseBanner = payload.showPricingIncreaseBanner;
      state.isTeamAccount = payload.isTeamAccount;
      state.isPremiumPlus = payload.isPremiumPlus;
      state.retentionOffer = payload.retentionOffer;
      state.cheaperPlans = payload.cheaperPlans;
      state.switchPlanTitle = payload.switchPlanTitle;
      state.switchPlanSubtitle = payload.switchPlanSubtitle;
      state.remainingCredits = payload.remainingCredits;
      state.remainingEmailSends = payload.remainingEmailSends;
      state.currentStep = meta.arg.initialStep;
    });
    builder.addCase(initCancelSubscriptionPanel.rejected, (state) => {
      state.hasInitFailed = true;
    });

    builder.addCase(cancelSubscription.pending, (state) => {
      state.isWaitingForAJAXResponse = true;
    });
    builder.addCase(cancelSubscription.fulfilled, (state) => {
      state.isWaitingForAJAXResponse = false;
    });
    builder.addCase(cancelSubscription.rejected, (state) => {
      state.isWaitingForAJAXResponse = false;
    });

    builder.addCase(pauseSubscription.pending, (state) => {
      state.isWaitingForAJAXResponse = true;
    });
    // A successful pause redirects away, so the CTA stays locked until navigation happens.
    builder.addCase(pauseSubscription.fulfilled, (state) => {
      state.isWaitingForAJAXResponse = true;
    });
    builder.addCase(pauseSubscription.rejected, (state) => {
      state.isWaitingForAJAXResponse = false;
    });

    builder.addCase(submitCancellationSupportRequest.pending, (state) => {
      state.isWaitingForAJAXResponse = true;
    });
    builder.addCase(submitCancellationSupportRequest.fulfilled, (state) => {
      state.isWaitingForAJAXResponse = false;
    });
    builder.addCase(submitCancellationSupportRequest.rejected, (state) => {
      state.isWaitingForAJAXResponse = false;
    });

    builder.addCase(submitPostCancellationFeedback.pending, (state) => {
      state.isWaitingForAJAXResponse = true;
    });
    builder.addCase(submitPostCancellationFeedback.fulfilled, (state) => {
      state.isWaitingForAJAXResponse = false;
    });
    builder.addCase(submitPostCancellationFeedback.rejected, (state) => {
      state.isWaitingForAJAXResponse = false;
    });

    // A successful switch redirects away, so there is deliberately no fulfilled case that unlocks.
    builder.addCase(getCheaperPlanChangeCost.pending, (state) => {
      state.isWaitingForAJAXResponse = true;
    });
    builder.addCase(getCheaperPlanChangeCost.rejected, (state) => {
      state.isWaitingForAJAXResponse = false;
    });
    builder.addCase(switchToCheaperPlan.rejected, (state) => {
      state.isWaitingForAJAXResponse = false;
    });
  },
});

export const {updateCancelReason, updateCancelReasonComments, setShuffledReasonIds, updateStep, updatePauseInterval, updateIsWaitingForAJAXResponse} = cancelSubscriptionPanelSlice.actions;
export const cancelSubscriptionPanelReducer = cancelSubscriptionPanelSlice.reducer;
